import React, { useState, useContext } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Divider,
  Chip,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff, Computer, LightMode, DarkMode, Login as LoginIcon } from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { ThemeContext } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authAPI } from '../../services/api';

const LoginPage = () => {
  const { login } = useContext(AuthContext);
  const { mode, toggleTheme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);

    if (result.success) {
      toast.success(`Welcome back, ${result.user.name}!`);
      if (result.user.role === 'student') {
        navigate('/student/dashboard');
      } else {
        navigate('/admin');
      }
    } else {
      setError(result.message);
      toast.error(result.message);
    }

    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setForgotPasswordLoading(true);
    try {
      await authAPI.forgotPassword(forgotPasswordEmail);
      toast.success('A temporary password has been sent to your email');
      setForgotPasswordOpen(false);
      setForgotPasswordEmail('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send temporary password');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #0D111C 0%, #1F2937 100%)'
            : 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
        py: 4,
        position: 'relative',
      }}
    >
      {/* Theme Toggle - Top Right */}
      <IconButton
        onClick={toggleTheme}
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          bgcolor: 'background.paper',
          boxShadow: 2,
          '&:hover': {
            bgcolor: 'background.paper',
            boxShadow: 4,
          },
        }}
      >
        {mode === 'light' ? <DarkMode /> : <LightMode />}
      </IconButton>

      <Container maxWidth="md">
        <Grid container spacing={0} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: 8 }}>
          {/* Left Side - Branding */}
          <Grid item xs={12} md={5}>
            <Paper
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                color: 'white',
                p: 5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: { xs: 250, md: 600 },
              }}
            >
              <Computer sx={{ fontSize: 80, mb: 3 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 800 }}>
                BSS EDU-ERP
              </Typography>
              <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
                Education Management System
              </Typography>
              <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', width: '60%', mb: 3 }} />
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 4 }}>
                Comprehensive platform for managing institutions, courses, students, and examinations
              </Typography>

              {/* User Type Indicators */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', mt: 'auto' }}>
                <Chip
                  label="Super Admin"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                  size="small"
                />
                <Chip
                  label="Branch Admin (ATC)"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                  size="small"
                />
                <Chip
                  label="Students"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                  size="small"
                />
              </Box>
            </Paper>
          </Grid>

          {/* Right Side - Login Form */}
          <Grid item xs={12} md={7}>
            <Card elevation={0} sx={{ height: '100%', minHeight: { xs: 'auto', md: 600 } }}>
              <CardContent sx={{ p: { xs: 4, md: 6 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: 'text.primary' }}>
                    Welcome Back
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Sign in to access your account
                  </Typography>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoFocus
                    sx={{ mb: 3 }}
                    placeholder="Enter your email"
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    sx={{ mb: 2 }}
                    placeholder="Enter your password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ textAlign: 'right', mb: 3 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      onClick={() => setForgotPasswordOpen(true)}
                    >
                      Forgot password?
                    </Link>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? null : <LoginIcon />}
                    sx={{
                      py: 1.8,
                      mb: 3,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                  </Button>

                  <Divider sx={{ my: 3 }}>
                    <Chip label="OR" size="small" />
                  </Divider>

                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Don't have an account?{' '}
                      <Link href="/#contact" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Contact us for enrollment
                      </Link>
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      onClick={() => navigate('/')}
                      variant="text"
                      sx={{ fontWeight: 600 }}
                    >
                      ← Back to Home
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Footer Branding */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Powered by
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: mode === 'dark'
                ? 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)'
                : 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Arvension Technologies LLP
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            © {new Date().getFullYear()} BSS IT Mission. All rights reserved.
          </Typography>
        </Box>
      </Container>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={() => !forgotPasswordLoading && setForgotPasswordOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Reset Password
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your registered email address. We will send a temporary password to your email.
          </Typography>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={forgotPasswordEmail}
            onChange={(e) => setForgotPasswordEmail(e.target.value)}
            placeholder="Enter your registered email"
            autoFocus
            disabled={forgotPasswordLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleForgotPassword();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setForgotPasswordOpen(false)}
            disabled={forgotPasswordLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleForgotPassword}
            disabled={forgotPasswordLoading}
          >
            {forgotPasswordLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Temporary Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginPage;
